import { CButton, CIcon, CText } from '@carrefour/design-system-components-react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { fetchCommunications } from '../../utils/fetcher';
import { Communication } from '../../types/Communication';
import { COLORS } from '../../utils/colors';

/* ===== Styled Components ===== */

const BannerContainer = styled.div<{ bannerColor?: string, isClickable?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => props.bannerColor || '#e8f1f8'};
  padding: 8px 16px;
  margin: 15px 15px 5px 15px;
  border-radius: 15px;
  font-family: system-ui;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  cursor: ${(props) => (props.isClickable ? 'pointer' : 'default')};
`;

const IconCustom = styled(CIcon)`
  font-size: 1.5rem;
  color: #173EB4;
`;

const LeftSection = styled.div<{ flexDirection?: string, gap?: string, alignItems?: string }>`
  display: flex;
  flex-direction: ${props => props.flexDirection || 'row'};
  gap: ${props => props.gap || '0px'};
  ${props => props.alignItems && `align-items: ${props.alignItems};`}
`;

const CTextCustom = styled(CText)<{ fontSize?: string, fontWeight?: string }>`
  font-family: system-ui !important;
  font-size: ${props => props.fontSize || '.85rem'} !important;
  font-weight: ${props => props.fontWeight || '400'};
  text-align: left;
  margin-bottom: 2px;
`;

const StyledLink = styled.a`
  cursor: pointer;
  text-decoration: none;
  color: #0970E6;
  white-space: nowrap;
  font-weight: bold;
  margin-left: auto;
  margin-right: 15px;
  margin-top: 10px;

  @media (min-width: 1024px) {
    margin-right: 44px;
  }
`;

/* ===== Component ===== */

const CommunicationBanner: React.FC = () => {

  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    const stored = sessionStorage.getItem('dismissed_communications');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });
  const [communications, setCommunications] = useState<Communication[]>([]);

  const isValidCommunication = (communication: Communication): boolean => {
    const now = new Date();
    const startDate = communication.startDate ? new Date(communication.startDate) : null;
    const endDate = communication.endDate ? new Date(communication.endDate) : null;

    const isAfterStart = !startDate || startDate <= now;
    const isBeforeEnd = !endDate || endDate >= now;

    return isAfterStart && isBeforeEnd;
  };

  const sortByCreatedDate = (a: Communication, b: Communication): number => {
    const dateA = new Date(a.startDate);
    const dateB = new Date(b.startDate);
    return dateB.getTime() - dateA.getTime();
  };

  const handleDismiss = (commId: string) => {
    setDismissedIds((prevDismissedIds) => {
      const newDismissedIds = new Set(prevDismissedIds);
      newDismissedIds.add(commId);
      sessionStorage.setItem('dismissed_communications', JSON.stringify(Array.from(newDismissedIds)));
      return newDismissedIds;
    });
  };

  useEffect(() => {
    fetchCommunications()
      .then((data) => {
        const filtredCommunications = (data ?? [])
          .filter(isValidCommunication)
          .sort(sortByCreatedDate);

        setCommunications(filtredCommunications);
      });
  }, []);

  const visibleCommunications = communications.filter(comm => !dismissedIds.has(comm.id));

  if (visibleCommunications.length === 0) return null;

  return (
    <>
      {visibleCommunications.map((comm) => (
        <BannerContainer 
          key={comm.id || comm.title} 
          data-testid="communication-banner"
          bannerColor={comm.type === "ALERT" ? COLORS.bg_red : "#E8F8FF"}
          isClickable={!!comm.link} 
          onClick={() => comm.link && window.open(comm.link, "_blank")}
        >
          <LeftSection gap="12px" alignItems="center" style={{ flex: 1 }}>
            <IconCustom name={'list'} />
            <LeftSection flexDirection="column" style={{ flex: 1 }}>
              <CTextCustom fontSize="1rem" fontWeight="800">{comm.title}</CTextCustom>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                <CTextCustom style={{ marginBottom: 0 }}>{comm.text}</CTextCustom>
              </div>
            </LeftSection>
            {comm.link && (
              <StyledLink
                href={comm.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                Ouvrir
              </StyledLink>
            )}
          </LeftSection>

          <CButton variation='tertiary' tone='monochrome'
            onClick={(e) => {
              e.stopPropagation(); // prevents triggering banner click
              handleDismiss(comm.id);
            }}
          >
            ✕
          </CButton>
        </BannerContainer>
      ))}
    </>
  );
};

export default CommunicationBanner;